import {
  baseFetch,
  canUseDOM,
  fetchWithAuth,
  fetchUserSettings,
  fetchUserFavorites,
  fetchUserDetails,
  getWindow,
  type UserLocation,
  type WavetrakOptions,
  findSubdivisionCode,
} from '@surfline/web-common';
import { Cookies } from 'react-cookie';
import { GeoLocationResponse } from 'types/location';
import type { WavetrakContext } from 'types/wavetrak';
import type {
  UserDetails,
  UserEntitlements,
  UserPermissionsResponse,
  UserSettings,
  UserState,
} from 'types/user';
import config from 'config';
import addCustomUserAgentHeader from 'utils/addCustomUserAgentHeader';
import addGeoHeaders from 'utils/addGeoHeaders';
import type { UserFavorites, UserFavoritesResponse } from 'types/userFavorites';
import getCloudflareGeo from 'utils/getCloudlfareGeo';
import { isNil, omitBy } from 'lodash';
import type { Permission } from 'types/permissions';

const shouldUsePublicSSR = () => process.env.KBYG_USE_PUBLIC_SERVICES_FOR_SSR === 'true';

const getSSRServiceUrls = (isServer: boolean) => {
  const usePublic = isServer && shouldUsePublicSSR();
  return {
    user: usePublic ? config.servicesURL : isServer ? config.userServiceURL : config.servicesURL,
    subscription: usePublic
      ? config.servicesURL
      : isServer
        ? config.subscriptionServiceURL
        : config.servicesURL,
    kbyg: `${config.servicesURL}/kbyg`,
    geoTarget: usePublic
      ? `${config.servicesURL}/geo-target`
      : isServer
        ? config.geoTargetServiceUrl
        : `${config.servicesURL}/geo-target`,
  };
};

// Request-scoped cache using WeakMap - automatically GC'd when request is done
type RequestCache = Map<string, UserState | null>;

// WeakMap is used to automatically GC'd when request is done, import to avoid
// steadily increasing memory usage.
const requestCacheMap = new WeakMap<any, RequestCache>();

export const fetchUserFavoritesWrapper = async (
  fetchOptions: RequestInit | undefined,
  wavetrakOptions: WavetrakOptions,
  kbygCustomUrl: string,
): Promise<{ favorites?: UserFavorites; error?: string }> => {
  try {
    const response = (await fetchUserFavorites(fetchOptions, {
      ...wavetrakOptions,
      customUrl: kbygCustomUrl,
    })) as UserFavoritesResponse;
    return {
      favorites: response?.data?.favorites ?? [],
    };
  } catch (error: any) {
    return {
      error: error?.message || 'Error fetching user favorites',
    };
  }
};

const fetchUserDetailsWrapper = async (
  fetchOptions: RequestInit | undefined,
  wavetrakOptions: WavetrakOptions,
  isAuthenticated: boolean,
  isServer: boolean,
  userServiceURL: string,
): Promise<{ details?: UserDetails; error?: string }> => {
  if (!isAuthenticated) return {};
  try {
    return {
      details: await fetchUserDetails(fetchOptions, {
        ...wavetrakOptions,
        customUrl: userServiceURL,
        useAccessTokenQueryParam: !isServer,
      }),
    };
  } catch (error: any) {
    return {
      error: error?.message || 'Error fetching user details',
    };
  }
};

const fetchUserSettingsWrapper = async (
  fetchOptions: RequestInit | undefined,
  wavetrakOptions: WavetrakOptions,
  isServer: boolean,
  userServiceURL: string,
): Promise<{ settings?: UserSettings; error?: string }> => {
  try {
    const response = (await fetchUserSettings(fetchOptions, {
      ...wavetrakOptions,
      customUrl: userServiceURL,
      useAccessTokenQueryParam: !isServer,
    })) as UserSettings;
    return {
      settings: response,
    };
  } catch (error: any) {
    console.error(`Error fetching user settings: ${error?.message}`, {
      fetchOptions,
      wavetrakOptions,
      isServer,
      userServiceURL,
    });
    return {
      error: error?.message || 'Error fetching user settings',
    };
  }
};

const fetchUserPermissionsWrapper = async (
  fetchOptions: RequestInit | undefined,
  wavetrakOptions: WavetrakOptions,
  isAuthenticated: boolean,
  isServer: boolean,
  subscriptionCustomUrl: string,
): Promise<{
  error?: string;
  permissions: Array<Permission>;
  entitlements: UserEntitlements;
  isGuestUser: boolean;
  ltaEntitlement?: string;
  ltaExpiresAt?: number;
  ltaType?: string;
  ltaShowBanner?: boolean;
  ltaBannerCopy?: string;
}> => {
  if (!isAuthenticated)
    return {
      permissions: [],
      entitlements: [],
      isGuestUser: false,
    };
  try {
    const response = (await fetchWithAuth('/permissions/user', fetchOptions, {
      ...wavetrakOptions,
      customUrl: subscriptionCustomUrl,
      useAccessTokenQueryParam: !isServer,
    })) as UserPermissionsResponse;
    return {
      permissions: response.permissions,
      entitlements: response.entitlement ? [response.entitlement] : [],
      isGuestUser: response.isGuestUser,
      ltaEntitlement: response?.ltaEntitlement,
      ltaExpiresAt: response?.ltaExpiresAt,
      ltaType: response?.ltaType,
      ltaShowBanner: response?.ltaShowBanner,
      ltaBannerCopy: response?.ltaBannerCopy,
    };
  } catch (error: any) {
    return {
      permissions: [],
      error: error?.message || 'Error fetching user permissions',
      entitlements: [],
      isGuestUser: false,
    };
  }
};
const fetchSubscriptionWarningsWrapper = async (
  fetchOptions: RequestInit | undefined,
  wavetrakOptions: WavetrakOptions,
  isAuthenticated: boolean,
  isServer: boolean,
  subscriptionCustomUrl: string,
): Promise<{
  error?: string;
  warnings: UserState['warnings'];
}> => {
  if (!isAuthenticated) return { warnings: [] };
  try {
    return {
      warnings: await fetchWithAuth('/payment/alerts?product=sl', fetchOptions, {
        ...wavetrakOptions,
        customUrl: subscriptionCustomUrl,
        useAccessTokenQueryParam: !isServer,
      }),
    };
  } catch (error: any) {
    return {
      warnings: [],
      error: error?.message || 'Error fetching user subscription warnings',
    };
  }
};

const fetchUserLocationWrapper = async (
  geoBaseUrl: string,
  ipAddress: string,
): Promise<{
  location?: UserLocation;
  subdivisions?: GeoLocationResponse['subdivisions'];
  error?: string;
}> => {
  try {
    const response = (await baseFetch(
      `${geoBaseUrl}/region?ipAddress=${ipAddress}`,
    )) as GeoLocationResponse;
    return { location: response?.location, subdivisions: response?.subdivisions };
  } catch (error: any) {
    return {
      error: error?.message || 'Error fetching user favorites',
    };
  }
};

const checkForFatalErrors = (
  fetchErrors: Record<string, string | undefined>,
  fatalError = false,
  isServer = false,
) => {
  const actualFetchErrors = omitBy(fetchErrors, isNil) as Record<string, string>;
  if (actualFetchErrors) {
    if (isServer) {
      const newrelic = require('newrelic');
      newrelic.addCustomAttributes(actualFetchErrors);
    } else {
      const newrelic = getWindow()?.newrelic;
      if (newrelic && typeof newrelic.addCustomAttributes === 'function') {
        newrelic.addCustomAttributes(actualFetchErrors);
      }
    }
  }

  if (fatalError) {
    const error = new Error('Fatal server-side fetchUserData error.');
    if (isServer) {
      const newrelic = require('newrelic');
      newrelic.noticeError(error, {
        ...actualFetchErrors,
      });
    } else {
      const newrelic = getWindow()?.newrelic;
      if (newrelic && typeof newrelic.noticeError === 'function') {
        getWindow()?.newrelic?.noticeError(error, { ...actualFetchErrors });
      }
    }
    throw error;
  }
};

const fetchAllUserData = async ({
  accessToken,
  userId,
  countryCode,
  clientIp,
  location,
  isServer = false,
}: {
  accessToken: string | null;
  userId: string | null | undefined;
  countryCode: string;
  clientIp: string;
  location: { latitude: number; longitude: number };
  isServer: boolean;
}): Promise<UserState | null> => {
  const isAuthenticated = isServer ? !!accessToken && !!userId : !!accessToken;
  const urls = getSSRServiceUrls(isServer);
  const fetchOptions = isServer
    ? {
        headers: {
          ...addCustomUserAgentHeader(isServer),
          ...addGeoHeaders(countryCode, location, isServer),
          ...(userId && { 'x-auth-userid': userId }),
        },
      }
    : {};

  // Do not forward a cookie the middleware rejected (no userId on server); that causes 401 on settings and a fatal error.
  const wavetrakOptions =
    (isServer || !canUseDOM) && isAuthenticated && accessToken
      ? {
          serverSideCookies: { access_token: accessToken },
        }
      : {};

  const [favorites, userDetails, userSettings, subscriptionWarnings, userLocation, permissions] =
    await Promise.all([
      fetchUserFavoritesWrapper(fetchOptions, wavetrakOptions, urls.kbyg),
      fetchUserDetailsWrapper(fetchOptions, wavetrakOptions, isAuthenticated, isServer, urls.user),
      fetchUserSettingsWrapper(fetchOptions, wavetrakOptions, isServer, urls.user),
      fetchSubscriptionWarningsWrapper(
        fetchOptions,
        wavetrakOptions,
        isAuthenticated,
        isServer,
        urls.subscription,
      ),
      fetchUserLocationWrapper(urls.geoTarget, clientIp),
      fetchUserPermissionsWrapper(
        fetchOptions,
        wavetrakOptions,
        isAuthenticated,
        isServer,
        urls.subscription,
      ),
    ]);

  const fatalError = userSettings?.error || permissions?.error;
  checkForFatalErrors(
    {
      detailsError: userDetails?.error,
      entitlementsError: permissions?.error,
      favoritesError: favorites?.error,
      settingsError: userSettings?.error,
      permissionsError: permissions?.error,
    },
    !!fatalError,
    isServer,
  );

  return {
    countryCode,
    subdivisionCode: userLocation?.subdivisions
      ? findSubdivisionCode(userLocation?.subdivisions)
      : null,
    ip: clientIp,
    // @ts-ignore: user details needs to be null when the user isn't set.
    details: userDetails?.details?._id
      ? {
          ...userDetails?.details,
          firstName: userDetails?.details?.firstName,
          lastName: userDetails?.details?.lastName,
          email: userDetails?.details?.email,
          _id: userDetails?.details?._id,
          isEmailVerified: userDetails?.details?.isEmailVerified,
          // We don't want the hashed/salted password in the store
          password: undefined,
        }
      : null,
    entitlements: permissions?.entitlements,
    favorites: favorites?.favorites ?? [],
    isGuestUser: permissions?.isGuestUser ?? false,
    // note: settings will never be undefined because we treat a settings error as a fatal error and throw an exception above
    settings: userSettings.settings as UserSettings,
    warnings: subscriptionWarnings.warnings,
    location: {
      ...location,
      time_zone: userLocation?.location?.time_zone ?? 'America/Los_Angeles',
    },
    permissions: permissions.permissions,
    ...(permissions?.ltaEntitlement && { ltaEntitlement: permissions.ltaEntitlement }),
    ...(permissions?.ltaExpiresAt && { ltaExpiresAt: permissions.ltaExpiresAt }),
    ...(permissions?.ltaType && { ltaType: permissions.ltaType }),
    ...(permissions?.ltaShowBanner !== undefined && { ltaShowBanner: permissions.ltaShowBanner }),
    ...(permissions?.ltaBannerCopy && { ltaBannerCopy: permissions.ltaBannerCopy }),
  };
};

export const getAccessToken = (req: any, _res?: any): string | null => {
  try {
    const win = getWindow();
    return !req && win?.document?.cookie
      ? new Cookies(win?.document?.cookie)?.get('access_token')
      : req?.cookies?.access_token || null;
  } catch (_error) {
    return null;
  }
};

export const getUserRegion = async () => {
  const url = `${config.servicesURL}/geo-target/region?`;
  return baseFetch(url);
};

export const getUser = async (
  context: WavetrakContext,
  isServer = false,
): Promise<UserState | null> => {
  const { req, res } = context;
  if (isServer && !req) {
    return null;
  }
  const accessToken = getAccessToken(req, res);
  const geoData = getCloudflareGeo(req);

  /* The Next.js middleware under /src/middleware.ts sets the `x-auth-userid` header
  in the response, but only if the user passes the authentication checks */
  const userId = res?.getHeader('x-auth-userid')?.toString() || null;
  const wasUserLoggedOut = res?.getHeader('x-auth-user-logged-out') === 'true';

  // Create cache key from all params that affect fetchAllUserData result (fast string concat)
  const cacheKey = `${accessToken}|${userId}|${geoData.countryCode}|${geoData.clientIp}|${geoData.location.latitude}|${geoData.location.longitude}`;

  // Check request-scoped cache (only works server-side with req object)
  if (isServer && req) {
    // Get or create cache for this request
    let requestCache = requestCacheMap.get(req);
    if (!requestCache) {
      requestCache = new Map<string, UserState | null>();
      requestCacheMap.set(req, requestCache);
    }

    // Check if we have cached userData for these exact params
    if (requestCache.has(cacheKey)) {
      const cachedUserData = requestCache.get(cacheKey) ?? null;
      if (cachedUserData) {
        cachedUserData.wasLoggedOut = wasUserLoggedOut;
      }
      return cachedUserData;
    }
  }

  const userData = await fetchAllUserData({
    accessToken,
    userId,
    countryCode: geoData.countryCode,
    clientIp: geoData.clientIp,
    location: geoData.location,
    isServer,
  });
  if (userData) {
    userData.wasLoggedOut = wasUserLoggedOut;
  }

  // Store userData result in request-scoped cache
  if (isServer && req) {
    const requestCache = requestCacheMap.get(req);
    if (requestCache) {
      requestCache.set(cacheKey, userData);
    }
  }

  return userData;
};

export default getUser;
